import type { StepFieldInfo } from "../types/step";

// Step 1: Profile step fields
export const profileFields: StepFieldInfo[] = [
	{ name: "name", required: true },
	{ name: "phoneNumber", required: true },
	{ name: "email" },
	{ name: "relationship", required: true },
	{
		name: "propertyType.type",
		required: true,
		conditionalRequirement: {
			dependsOn: "relationship",
			whenValue: ["owner", "agent"],
		},
	},
	{
		name: "propertyType.subType",
		required: true,
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa", "plot"],
		},
	},
	{
		name: "propertyType.houseType",
		required: true,
		conditionalRequirement: {
			dependsOn: "propertyType.subType",
			whenValue: ["h:p-", "h:i-"],
		},
	},
	{
		name: "isCommunity",
		conditionalRequirement: {
			dependsOn: "propertyType.subType",
			whenValue: ["h:p-"],
		},
	},
	{
		name: "entityType",
		conditionalRequirement: {
			dependsOn: "relationship",
			whenValue: ["builder", "landOwner", "investor"],
		},
	},
	{
		name: "companyName",
		conditionalRequirement: {
			dependsOn: "relationship",
			whenValue: ["builder", "landOwner", "investor"],
		},
	},
	{
		name: "officeAddress",
		conditionalRequirement: {
			dependsOn: "relationship",
			whenValue: ["builder", "landOwner", "investor"],
		},
	},
	{
		name: "gstNo",
		conditionalRequirement: {
			dependsOn: "relationship",
			whenValue: ["builder", "landOwner", "investor"],
		},
	},
];

// Step 2: Address step fields
export const addressFields: StepFieldInfo[] = [
	{ name: "projectName", required: true },
	{ name: "location.locality", required: true },
	{ name: "location.address" },
	{ name: "location.city", required: true },
	{ name: "location.pin", required: true },
	{ name: "location.coordinates", required: true },
	{ name: "flatNumber" },
	{ name: "blockName" },
];

// Step 3: Basic Property Details step fields
export const basicFields: StepFieldInfo[] = [
	{
		name: "bedrooms",
		required: true,
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
	{
		name: "bathrooms",
		required: true,
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
	{
		name: "balconies",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
	{
		name: "numParking",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
	{
		name: "coveredParking",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
	{
		name: "subArea",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
	{
		name: "carpetArea",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
	{ name: "price", required: true },
	{
		name: "plotArea",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["plot", "house", "villa"],
		},
	},
	{
		name: "facing",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
	{
		name: "plotFacing",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["plot", "house", "villa"],
		},
	},
	{ name: "possessionStatus" },
	{
		name: "constructedOn",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
	{
		name: "specialAttributes",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
];

// Step 4: Additional Property Details step fields
export const additionalFields: StepFieldInfo[] = [
	{
		name: "totalFloors",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
	{
		name: "totalBasements",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
	{ name: "registered" },
	{
		name: "isRectangular",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["plot", "house", "villa"],
		},
	},
	{
		name: "isCornerPlot",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["plot", "house", "villa"],
		},
	},
	{
		name: "plotSides",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["plot", "house", "villa"],
		},
	},
	{
		name: "flooring",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
	{
		name: "furnishing",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "house", "villa"],
		},
	},
	{ name: "notes" },
];

// Step 5: Project/House/Community Details step fields
export const otherFields: StepFieldInfo[] = [
	{ name: "approachRoadWidth", required: true },
	{ name: "numEntrances" },
	{ name: "totalFloors" },
	{ name: "totalBasements" },
	{ name: "waterSource" },
	// Amenities shown when: apartment OR villa OR (isCommunity=true AND house)
	{
		name: "amenities",
		conditionalRequirement: {
			dependsOn: "propertyType.type",
			whenValue: ["apartment", "villa", "house"],
		},
	},
	{ name: "approvals" },
	{ name: "documents.approvalDocs" },
	{ name: "documents.constructionKhathaAuthority", required: true },
	{ name: "documents.constructionKhatha", required: true },
	{ name: "documents.landKhathaAuthority", required: true },
	{ name: "documents.landKhatha", required: true },
];

// Step 6: Uploads step fields
export const uploadFields: StepFieldInfo[] = [{ name: "images" }];

// Step 7: Fee Confirmation step fields
export const feeFields: StepFieldInfo[] = [
	{ name: "isConfirmationDone", required: true }, // this is not a form field, but it is used to show the fee confirmation as done in the progress-bar
];

// Step 8: Slot Confirmation step fields
export const slotFields: StepFieldInfo[] = [
	{ name: "flatNumber" },
	{ name: "blockName" },
	{ name: "isSelfOccupied", required: true },
	{
		name: "keyholderName",
		conditionalRequirement: {
			dependsOn: "isSelfOccupied",
			whenValue: "no",
		},
	},
	{
		name: "keyholderMobile",
		conditionalRequirement: {
			dependsOn: "isSelfOccupied",
			whenValue: "no",
		},
	},
	{
		name: "keyholderDesignation",
		conditionalRequirement: {
			dependsOn: "isSelfOccupied",
			whenValue: "no",
		},
	},
	{ name: "availabilitySlots", required: true },
];

// Step 9: Success step has no fields
export const successFields: StepFieldInfo[] = [];

// Investor/Landowner/Builder-specific fields for Project Details step
export const builderOrLandownerOrInvestorProjectFields: StepFieldInfo[] = [
	{ name: "projects.0.name", required: true },
	{ name: "projects.0.totalFlats", required: true },
	{ name: "projects.0.noOfFlatsForSale", required: true },
	{ name: "projects.0.propertyTypes", required: true },
];

export const stepFieldsMap: Record<string, StepFieldInfo[]> = {
	profile: profileFields,
	address: addressFields,
	basic: basicFields,
	additional: additionalFields,
	other: otherFields,
	uploads: uploadFields,
	fee: feeFields,
	slot: slotFields,
	success: successFields,
	builderOrLandownerOrInvestorProject: builderOrLandownerOrInvestorProjectFields,
};
